import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { getStructure, TutorStructureNode } from '../../../services/tutorStructureService';
import { CreateTestRequest } from '../../../services/tutorScheduledTestService';

interface Props {
  courseId: number;
  onSubmit: (req: CreateTestRequest) => Promise<void>;
  onCancel: () => void;
}

function flattenTopicNodes(nodes: TutorStructureNode[]): TutorStructureNode[] {
  const result: TutorStructureNode[] = [];
  for (const node of nodes) {
    if (node.nodeType === 'TOPIC_REF' || node.nodeType === 'CUSTOM_TOPIC') {
      result.push(node);
    }
    if (node.children?.length) {
      result.push(...flattenTopicNodes(node.children));
    }
  }
  return result;
}

export default function CreateTestForm({ courseId, onSubmit, onCancel }: Props) {
  const [name, setName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [randomizationMode, setRandomizationMode] = useState<'SHARED' | 'PER_STUDENT'>('SHARED');
  const [questionCount, setQuestionCount] = useState<string>('10');
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<string>('');
  const [selectedNodeIds, setSelectedNodeIds] = useState<number[]>([]);

  const [availableNodes, setAvailableNodes] = useState<TutorStructureNode[]>([]);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    getStructure(courseId)
      .then((s) => setAvailableNodes(flattenTopicNodes(s.nodes)))
      .catch(() => setError('Failed to load course topics'))
      .finally(() => setLoadingNodes(false));
  }, [courseId]);

  const toggleNode = (nodeId: number) => {
    setSelectedNodeIds((prev) =>
      prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId],
    );
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Test name is required';
    if (!scheduledDate) errs.scheduledDate = 'Scheduled date is required';
    if (selectedNodeIds.length === 0) errs.topics = 'Select at least one topic';
    const qc = parseInt(questionCount, 10);
    if (!questionCount || isNaN(qc) || qc < 1) errs.questionCount = 'Enter a valid question count (≥ 1)';
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      return;
    }
    setValidationErrors({});
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        scheduledDate,
        topicNodeIds: selectedNodeIds,
        randomizationMode,
        questionCount: parseInt(questionCount, 10),
        timeLimitMinutes: timeLimitMinutes ? parseInt(timeLimitMinutes, 10) : null,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create test';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Create Scheduled Test
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        label="Test name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        error={!!validationErrors.name}
        helperText={validationErrors.name}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Scheduled date"
        type="date"
        value={scheduledDate}
        onChange={(e) => setScheduledDate(e.target.value)}
        fullWidth
        required
        InputLabelProps={{ shrink: true }}
        error={!!validationErrors.scheduledDate}
        helperText={validationErrors.scheduledDate}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Randomization mode</InputLabel>
        <Select
          value={randomizationMode}
          label="Randomization mode"
          onChange={(e) => setRandomizationMode(e.target.value as 'SHARED' | 'PER_STUDENT')}
        >
          <MenuItem value="SHARED">SHARED — same questions for all students</MenuItem>
          <MenuItem value="PER_STUDENT">PER_STUDENT — unique subset per student</MenuItem>
        </Select>
        <FormHelperText>
          SHARED draws questions at creation time; PER_STUDENT draws at attempt start.
        </FormHelperText>
      </FormControl>

      <TextField
        label="Question count"
        type="number"
        value={questionCount}
        onChange={(e) => setQuestionCount(e.target.value)}
        fullWidth
        inputProps={{ min: 1 }}
        error={!!validationErrors.questionCount}
        helperText={validationErrors.questionCount || 'Capped at pool size if larger than available questions'}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Time limit (minutes, optional)"
        type="number"
        value={timeLimitMinutes}
        onChange={(e) => setTimeLimitMinutes(e.target.value)}
        fullWidth
        inputProps={{ min: 1 }}
        helperText="Leave blank for no time limit"
        sx={{ mb: 2 }}
      />

      <FormControl
        component="fieldset"
        error={!!validationErrors.topics}
        sx={{ mb: 2, width: '100%' }}
      >
        <FormLabel component="legend">Topics (select at least one)</FormLabel>
        {loadingNodes ? (
          <CircularProgress size={24} sx={{ mt: 1 }} />
        ) : availableNodes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No topic nodes found in this course structure.
          </Typography>
        ) : (
          <FormGroup>
            {availableNodes.map((node) => (
              <FormControlLabel
                key={node.id}
                control={
                  <Checkbox
                    checked={selectedNodeIds.includes(node.id)}
                    onChange={() => toggleNode(node.id)}
                  />
                }
                label={node.label || `Node ${node.id}`}
              />
            ))}
          </FormGroup>
        )}
        {validationErrors.topics && <FormHelperText>{validationErrors.topics}</FormHelperText>}
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={submitting || loadingNodes}>
          {submitting ? <CircularProgress size={20} /> : 'Create Test'}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
